import { useEffect, useRef, useState } from 'react';
import { NO_AUTOFILL } from './FilterPopover';

const DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Whether `text` is a complete `yyyy-mm-dd` that exists on the calendar. */
function isValidDay(text: string): boolean {
  const match = DAY_PATTERN.exec(text);
  if (!match) return false;
  const [year, month, day] = match.slice(1).map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

/** Digits typed in any shape → `yyyy-mm-dd` as far as they go (dashes inserted). */
function autoFormat(text: string): string {
  const digits = text.replace(/\D/g, '').slice(0, 8);
  return [digits.slice(0, 4), digits.slice(4, 6), digits.slice(6, 8)].filter(Boolean).join('-');
}

interface Props {
  /** `yyyy-mm-dd`, or null when empty. */
  value: string | null;
  onChange: (next: string | null) => void;
  label: string;
  min?: string;
  max?: string;
}

/**
 * Day field that always reads `yyyy-mm-dd`, whatever the browser's language
 * (a native date input follows the browser's format and cannot be told
 * otherwise). Typing fills the dashes in; the calendar button opens the
 * browser's own date picker through a hidden native input. Only a complete,
 * real day reaches `onChange`: an impossible one is flagged, and leaving the
 * field with an unfinished or impossible day puts the last valid one back.
 */
export function DateInput({ value, onChange, label, min, max }: Props) {
  const [text, setText] = useState(value ?? '');
  const pickerRef = useRef<HTMLInputElement>(null);

  // Follow changes made elsewhere (calendar, Clear, the time switch)
  useEffect(() => {
    setText(value ?? '');
  }, [value]);

  const handleTextChange = (raw: string) => {
    // Keep what was typed when deleting, so a dash can be removed
    const next = raw.length < text.length ? raw : autoFormat(raw);
    setText(next);
    if (next === '') onChange(null);
    else if (isValidDay(next)) onChange(next);
  };

  const openCalendar = () => {
    try {
      pickerRef.current?.showPicker();
    } catch {
      // showPicker unsupported or blocked: typing still works
    }
  };

  const invalid = text.length === 10 && !isValidDay(text);

  return (
    <div className="date-input">
      <input
        type="text"
        className={`column-filter-input ${invalid ? 'invalid' : ''}`}
        {...NO_AUTOFILL}
        inputMode="numeric"
        placeholder="yyyy-mm-dd"
        maxLength={10}
        aria-label={label}
        aria-invalid={invalid}
        value={text}
        onChange={(e) => handleTextChange(e.target.value)}
        onBlur={() => setText(value ?? '')}
      />
      <button type="button" className="date-input-calendar" aria-label={`${label}: open calendar`} onClick={openCalendar}>
        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
          <rect x="2" y="3" width="12" height="11" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M2 6.5h12M5 1.5v3M11 1.5v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>
      <input
        ref={pickerRef}
        type="date"
        className="date-input-native"
        tabIndex={-1}
        aria-hidden="true"
        value={value ?? ''}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value || null)}
      />
    </div>
  );
}
